import type { ChangeOrderLoanStatusRow, PartnerSyncChecklist } from '../../../../store/oneButtonStore';
import { isPartnerSyncCompleteForConfig } from './partnerSyncConfig';

export type CommerceGuideSection =
    | 'financing'
    | 'partnerSync'
    | 'products'
    | 'quotes'
    | 'agreements'
    | 'changeOrder'
    | 'none';

export const COMMERCE_GUIDE_SELECTORS: Record<Exclude<CommerceGuideSection, 'none'>, string> = {
    financing: '[data-obd-guide="financing-grid"]',
    partnerSync: '[data-obd-guide="partner-sync"]',
    products: '[data-obd-guide="commerce-products"]',
    quotes: '[data-obd-guide="commerce-quotes"]',
    agreements: '[data-obd-guide="commerce-agreements"]',
    changeOrder: '[data-obd-guide="change-order"]',
};

/** Trinity `utility-info` / Angular action-button-group: `Is_Change_Order` on a `getLoanStatus` row. */
export function loanRowHasChangeOrderFlag(row: ChangeOrderLoanStatusRow): boolean {  
    return row.Is_Change_Order === true || row.is_Change_Order === true;
}

export function hasPendingChangeOrderFlag(details: ChangeOrderLoanStatusRow[] | null | undefined): boolean {
    if (!details?.length) return false;
    return details.some(loanRowHasChangeOrderFlag);
}

export function changeOrderPartnerLabels(details: ChangeOrderLoanStatusRow[] | null | undefined): string[] {
    if (!details?.length) return [];
    return details
        .filter(loanRowHasChangeOrderFlag)
        .map((d) => String(d.Finance_Partner_Name ?? d.finance_Partner_Name ?? '').trim())
        .filter(Boolean);
}

function changeOrderGuideMessage(details: ChangeOrderLoanStatusRow[] | null | undefined): string {
    const labels = changeOrderPartnerLabels(details);
    const who = labels.length ? labels.join(', ') : 'finance partner';
    return `Next: A change order is active for ${who}. Open Agreements to review status, and open Quotes if utility or system design changed so lines stay in sync.`;
}

function needsProductsAttention(opp: any, selectedCells: Set<string>): boolean {
    if (!opp || selectedCells.size === 0) return false;
    const raw = opp.OpportunityProduct ?? opp.opportunityProduct ?? [];
    const arr = Array.isArray(raw) ? raw : [];
    const active = arr.filter((p: any) => p.ActiveFlag !== false && p.activeFlag !== false);
    const productsNeeded = new Set<string>();
    selectedCells.forEach((key) => {
        const [, pt] = key.split('::');
        if (pt) productsNeeded.add(pt);
    });
    const covered = new Set<string>();
    for (const p of active) {
        const pt = String(p.Product_Type_Name ?? p.product_Type_Name ?? '').trim();
        if (productsNeeded.has(pt)) covered.add(pt);
    }
    for (const pt of productsNeeded) {
        if (!covered.has(pt)) return true;
    }
    return false;
}

export interface CommerceGuideInputs {
    selectedFinanceCells: Set<string>;
    financePartnerSyncStatus: Record<string, PartnerSyncChecklist>;
    opportunityDetails: any;
    dashboardQuotesSummaryLines: {
        selectedQuoteLine: string | null;
        signedContractLine: string | null;
    };
    /** From Zustand — synced on every successful Contract `getLoanStatus` by opportunity. */
    changeOrderDetails?: ChangeOrderLoanStatusRow[] | null;
}

/** Same priority order as the live footer copy — drives both message and guide-bot target. */
export function computeCommerceGuide(input: CommerceGuideInputs): { section: CommerceGuideSection; message: string } {
    const cells = input.selectedFinanceCells;
    if (cells.size === 0) {
        return {
            section: 'financing',
            message:
                'Next: In Financing Options, pick at least one partner and product for each line you want to price or quote.',
        };
    }
    const partners = new Set<string>();
    cells.forEach((key) => {
        const p = key.split('::')[0];
        if (p) partners.add(p);
    });
    for (const partner of partners) {
        const st = input.financePartnerSyncStatus[partner];
        if (!isPartnerSyncCompleteForConfig(partner, st)) {
            return {
                section: 'partnerSync',
                message: `Next: Finish Finance Partner Sync for ${partner} (lead, utility, contact, and partner-specific steps). What to check: open that partner's card in Commerce hub and clear every pending item.`,
            };
        }
    }
    if (needsProductsAttention(input.opportunityDetails, cells)) {
        return {
            section: 'products',
            message:
                'Next: Open Products and set finance partner and method per product so Salesforce matches your selected financing lines.',
        };
    }
    const selQuote = input.dashboardQuotesSummaryLines?.selectedQuoteLine?.trim();
    const signed = input.dashboardQuotesSummaryLines?.signedContractLine?.trim();
    const quoteIncomplete = !selQuote || selQuote === '—';
    const signedIncomplete = !signed || signed === '—';
    const co = hasPendingChangeOrderFlag(input.changeOrderDetails);
    const coMsg = co ? changeOrderGuideMessage(input.changeOrderDetails) : null;

    if (co && coMsg) {
        if (quoteIncomplete || signedIncomplete) {
            return { section: 'changeOrder', message: coMsg };
        }
    }

    if (quoteIncomplete) {
        return {
            section: 'quotes',
            message:
                'Next: Open Quotes to add or review lines and use partner tools. Use row actions to attach a contract when you are ready.',
        };
    }
    if (signedIncomplete) {
        return {
            section: 'agreements',
            message:
                'Next: Review Agreements for RetrieveContract data and signed status. What to check: application and contract rows for each partner.',
        };
    }
    if (co && coMsg) {
        return { section: 'changeOrder', message: coMsg };
    }
    return {
        section: 'none',
        message:
            'You are in good shape. Use Products, Quotes, or Agreements any time to adjust lines or review contracts.',
    };
}
